// Canonical Kelimo scoring engine.
// Gameplay rules: docs/GAME_RULES.md

const SCORE_BY_LENGTH = Object.freeze({
  2: 1,
  3: 3,
  4: 6,
  5: 10,
  6: 15,
  7: 21,
  8: 28,
  9: 36,
  10: 45,
});

const normalizeWord = (value) => String(value || '').toLocaleLowerCase('tr-TR');

const getWordScore = (word) => SCORE_BY_LENGTH[String(word || '').length] || 0;

/**
 * Finds the highest-scoring set of non-overlapping valid words in one
 * five-cell row/column. Empty cells are hard separators and MUST NOT be
 * collapsed; letters on opposite sides of an empty cell are not adjacent.
 */
const scoreLine = (cells, dictionary, lineType = 'satır', lineIndex = 0, axis = 'row', bonuses = []) => {
  if (!Array.isArray(cells)) return [];

  const candidates = [];
  let segmentStart = 0;

  while (segmentStart < cells.length) {
    while (
      segmentStart < cells.length &&
      (typeof cells[segmentStart] !== 'string' || cells[segmentStart].trim() === '')
    ) {
      segmentStart++;
    }

    if (segmentStart >= cells.length) break;

    let segmentEnd = segmentStart;
    while (
      segmentEnd < cells.length &&
      typeof cells[segmentEnd] === 'string' &&
      cells[segmentEnd].trim() !== ''
    ) {
      segmentEnd++;
    }

    for (let start = segmentStart; start < segmentEnd; start++) {
      for (let end = start + 2; end <= segmentEnd; end++) {
        const word = normalizeWord(cells.slice(start, end).join(''));
        if (dictionary.has(word)) {
          candidates.push({
            word,
            start,
            end,
            baseScore: getWordScore(word),
            multiplier: 2 ** bonuses.filter(p => (axis === 'row' ? p.row : p.col) === lineIndex && (axis === 'row' ? p.col : p.row) >= start && (axis === 'row' ? p.col : p.row) < end).length,
            score: getWordScore(word) * 2 ** bonuses.filter(p => (axis === 'row' ? p.row : p.col) === lineIndex && (axis === 'row' ? p.col : p.row) >= start && (axis === 'row' ? p.col : p.row) < end).length,
            type: `${lineType} ${lineIndex + 1} (${start + 1}-${end})`,
            axis,
            lineIndex,
            cells: Array.from({ length: end - start }, (_, offset) =>
              axis === 'row'
                ? { row: lineIndex, col: start + offset }
                : { row: start + offset, col: lineIndex }
            ),
          });
        }
      }
    }

    segmentStart = segmentEnd + 1;
  }

  const getNonOverlappingCombos = (words, start = 0, used = []) => {
    const results = [used];

    for (let i = start; i < words.length; i++) {
      const current = words[i];
      const overlaps = used.some(
        (existing) => !(existing.end <= current.start || existing.start >= current.end)
      );

      if (!overlaps) {
        results.push(...getNonOverlappingCombos(words, i + 1, [...used, current]));
      }
    }

    return results;
  };

  const allCombos = getNonOverlappingCombos(candidates);
  let bestCombo = [];
  let bestScore = 0;
  let bestCoveredLetters = 0;

  for (const combo of allCombos) {
    const score = combo.reduce((sum, word) => sum + word.score, 0);
    const coveredLetters = combo.reduce((sum, word) => sum + (word.end - word.start), 0);

    if (
      score > bestScore ||
      (score === bestScore && coveredLetters > bestCoveredLetters) ||
      (score === bestScore && coveredLetters === bestCoveredLetters && combo.length < bestCombo.length)
    ) {
      bestScore = score;
      bestCoveredLetters = coveredLetters;
      bestCombo = combo;
    }
  }

  return bestCombo;
};

const calculateScore = (board, dictionary, options = {}) => {
  const bonuses = options.bonusCells || [];
  if (!Array.isArray(board) || ![5, 7, 8, 9, 10].includes(board.length) || board.some((row) => !Array.isArray(row) || row.length !== board.length)) {
    throw new Error('Kelimo scoring requires a square 5, 7, 9 or 10 cell board.');
  }

  let totalScore = 0;
  const details = [];

  for (let row = 0; row < board.length; row++) {
    const words = scoreLine(board[row], dictionary, 'satır', row, 'row', bonuses);
    for (const word of words) {
      totalScore += word.score;
      details.push(word);
    }
  }

  for (let col = 0; col < board.length; col++) {
    const cells = board.map((row) => row[col]);
    const words = scoreLine(cells, dictionary, 'sütun', col, 'col', bonuses);
    for (const word of words) {
      totalScore += word.score;
      details.push(word);
    }
  }

  return { totalScore, details };
};

export {
  SCORE_BY_LENGTH,
  getWordScore,
  scoreLine,
  calculateScore,
};
